// @flow
import * as React from 'react';
import { TextField } from '@material-ui/core';
import type { UseComboboxProps } from 'downshift';
import styled, { type StyledComponent } from 'styled-components';
import {
  DangerHelperText,
  HelperText,
} from '../../../internal/internal-typography';
import type { DataProvidesProps } from '../../../utils/constants';
import { getDataProvidesProps } from '../../../utils/constants';
import useMuiFieldTheme from '../text_input/material_ui_theme';
import BaseFlyout from './flyout';
import SelectedChips from './selected_chips';
import textInputTheme from '../text_input/theme';
import EndAdornment from './end_adornment';
import type { Item } from './item';

type UseMultiDropdownProps = UseComboboxProps<Item> & {
  items: Array<Item>,
};

const FieldWrapper: StyledComponent<*, *, *> = styled.div`
  min-width: 300px;
`;

const MultiDropdown = ({ children }: { children: React.Node }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);

  return (
    <FieldWrapper ref={setAnchorEl}>
      {React.Children.map(children, (child) =>
        React.cloneElement(child, { anchorEl })
      )}
    </FieldWrapper>
  );
};
MultiDropdown.displayName = 'MultiDropdown';

type MultiDropdownFlyoutProps = {
  dropdownProps?: UseMultiDropdownProps,
  noResultsComponent?: React.Node,
  ...DataProvidesProps,
};

MultiDropdown.Flyout = (props: MultiDropdownFlyoutProps) => {
  return <BaseFlyout {...props} />;
};
MultiDropdown.Flyout.displayName = 'MultiDropdown.Flyout';

type MultiDropdownFieldProps = {|
  dropdownProps: UseMultiDropdownProps,
  label: string,
  helperText?: string,
  error?: boolean,
  required?: boolean,
  readOnly?: boolean,
  disabled?: boolean,
  fullWidth?: boolean,
  ...DataProvidesProps,
|};

const StartAdornment = ({
  selectedItems,
  removeSelectedItem,
  getSelectedItemProps,
}) => {
  return (
    <SelectedChips
      selectedItems={selectedItems}
      removeSelectedItem={removeSelectedItem}
      getSelectedItemProps={getSelectedItemProps}
    />
  );
};

MultiDropdown.Field = ({
  dropdownProps,
  ...props
}: MultiDropdownFieldProps) => {
  const {
    getComboboxProps,
    getDropdownProps,
    getInputProps,
    getLabelProps,
    getSelectedItemProps,
    isOpen,
    openMenu,
    toggleMenu,
    removeSelectedItem,
    selectedItems,
    reset,
    loadInitialOptions,
  } = dropdownProps;

  const {
    label,
    helperText,
    error = false,
    required = false,
    disabled = false,
    readOnly = false,
    fullWidth = true,
  } = props;

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const classes = useMuiFieldTheme(props);
  const HelperTextComponent = !error ? HelperText : DangerHelperText;

  return (
    <div {...getComboboxProps()}>
      <StyledTextField
        {...getDataProvidesProps(props)}
        label={label}
        error={error}
        required={required}
        disabled={disabled}
        variant="filled"
        size="medium"
        $isOpen={dropdownProps.isOpen}
        fullWidth={fullWidth}
        helperText={
          helperText && <HelperTextComponent>{helperText}</HelperTextComponent>
        }
        FormHelperTextProps={{ className: classes.helperText }}
        inputProps={{ className: classes.htmlInput, readOnly }}
        InputProps={{
          ...getInputProps({
            ...getDropdownProps(),
            refKey: 'inputRef',
          }),
          onFocus: () => {
            if (!isOpen) {
              openMenu();
              loadInitialOptions();
            }
          },
          onClick: () => {
            if (!isOpen) {
              openMenu();
              loadInitialOptions();
            }
          },
          startAdornment: (
            <StartAdornment
              selectedItems={selectedItems}
              removeSelectedItem={removeSelectedItem}
              getSelectedItemProps={getSelectedItemProps}
            />
          ),
          endAdornment: (
            <EndAdornment
              toggleMenu={toggleMenu}
              reset={reset}
              singleSelect={false}
              selectedItems={selectedItems}
              disabled={disabled}
            />
          ),
          classes: {
            root: classes.inputWrapper,
            underline: classes.inputWrapperUnderline,
            adornedStart: classes.adornedStart,
            adornedEnd: classes.adornedEnd,
          },
        }}
        InputLabelProps={{
          ...getLabelProps(),
          classes: {
            root: classes.inputLabel,
            asterisk: classes.requiredAsterisk,
            focused: classes.focusedInputLabel,
          },
          disableAnimation: true,
          shrink: false,
        }}
      />
    </div>
  );
};
MultiDropdown.Field.displayName = 'MultiDropdown.Field';

const StyledTextField = styled(TextField)`
  ${({ $isOpen }) =>
    $isOpen &&
    `
  &&&&&&&&&& .MuiInputBase-root {
    border-radius: 4px 4px 0 0;
    border-color: ${textInputTheme.default.borderColor};
  }
  `}
`;

export default MultiDropdown;
